import React, { useEffect, useRef } from 'react';

import AddVehicleView, { AddVehicleViewProps } from '../components/add-vehicle/AddVehicleView';
import { DiscardChangesView } from '../../../shared/components/discard-changes/DiscardChangesView';
import { DiscardLayerData, useDiscardLayer } from './hooks/useDiscardLayer';
import { GarageLayerContentView } from '../components/GarageLayerContent/GarageLayerContentView';
import { GarageLayerData } from '../components/GarageLayerContent/hooks/useGarageLayerData';
import { LayerContentWrapper } from '../../../shared/components/layer-content-wrapper/LayerContentWrapper';
import { NicknameLayerData } from './hooks/useNicknameLayer';
import { RemoveVehicleLayerData } from './hooks/useRemoveVehicleLayer';
import { createBridgeFocusLayerRender } from '../../../shared/utils/create-bridge-focus-layer-render';
import { createBridgeLayerRender } from '../../../shared/utils/create-bridge-layer-render';
import { useFeatureAppConfig } from '../../../shared/hooks/use-feature-app-config';
import { useFeatureServices } from '../../../shared/hooks/use-feature-services';
import { useGarageLayer } from './hooks/useGarageLayer';
import { useLayerManager } from '../../../shared/hooks/use-layer-manager';
import { useMyVwContext } from '../../../shared/hooks/use-my-vw-context';
import { useNicknameLayer } from './hooks/useNicknameLayer';
import { useRemoveVehicleLayer } from './hooks/useRemoveVehicleLayer';
import { useTrackingManager } from '../../../shared/hooks/use-tracking-manager';
import { ShimBackgroundTypeV2 } from '@volkswagen-onehub/layer-manager';

export type VehicleManagementMode = 'garage' | 'addVehicle' | 'nickname' | 'removeVehicle' | null;

const discardLayerRender = createBridgeFocusLayerRender<DiscardLayerData>(
  (data) => (
    <DiscardChangesView
      translationHandler={data.translationHandler}
      onDiscard={data.onDiscard}
      onKeepEditing={data.onKeepEditing}
    />
  ),
);

const garageLayerRender = createBridgeLayerRender<GarageLayerData>(
  (data) => data.translationHandler('garage.layer.title'),
  (data) => (
    <GarageLayerContentView
      vehicles={data.vehicles}
      preferredVehicle={data.preferredVehicle}
      translationHandler={data.translationHandler}
      trackingManager={data.trackingManager}
      onVehicleAction={data.onVehicleAction}
      onAddVehicle={data.onAddVehicle}
      onNavigateToVehicleManagement={data.onNavigateToVehicleManagement}
    />
  ),
);

const nicknameLayerRender = createBridgeLayerRender<NicknameLayerData>(
  (data) => data.title,
  (data) => data.content,
  (data) => data.onCloseAttempt,
);

const removeVehicleLayerRender = createBridgeLayerRender<RemoveVehicleLayerData>(
  (data) => data.title,
  (data) => data.content,
  (data, close) => (data.isDeactivating ? undefined : close),
);

interface VehicleManagementLayersProps {
  mode: VehicleManagementMode;
  onClose: () => void;
}

export const useVehicleManagementLayers = ({
  mode,
  onClose,
}: VehicleManagementLayersProps) => {
  const layerManager = useLayerManager();
  const addVehicleHasOpenedRef = useRef(false);
  const { translationHandler } = useMyVwContext();
  const { faServicesUrl = '' } = useFeatureAppConfig();
  const { 'locale-service': localeService } = useFeatureServices();
  const trackingManager = useTrackingManager();

  const { openDiscardLayer } = useDiscardLayer(discardLayerRender);

  useGarageLayer(mode === 'garage', onClose, garageLayerRender);

  useNicknameLayer(mode === 'nickname', onClose, openDiscardLayer, nicknameLayerRender);

  useRemoveVehicleLayer(mode === 'removeVehicle', onClose, removeVehicleLayerRender);

  useEffect(() => {
    if (mode === 'addVehicle' && !addVehicleHasOpenedRef.current && layerManager) {
      addVehicleHasOpenedRef.current = true;
      const addVehicleData: AddVehicleViewProps = {
        translationHandler,
        trackingManager,
        isCanada: localeService?.countryCode === 'CA',
        baseUrl: faServicesUrl,
      };
      layerManager.openInteractionLayer(
        (state: AddVehicleViewProps, close: any) => (
          <LayerContentWrapper
            title={state.translationHandler('vehicleManagement.interaction.layer.add.vehicle.title')}
            onClose={() => close()}
          >
            <AddVehicleView {...state} />
          </LayerContentWrapper>
        ),
        addVehicleData,
        {
          size: 'E' as any,
          shimBackgroundType: ShimBackgroundTypeV2.OPAQUE,
          onClose: (_state: any, _response: any) => {
            addVehicleHasOpenedRef.current = false;
            onClose();
          },
        },
      );
    }
    if (mode !== 'addVehicle') addVehicleHasOpenedRef.current = false;
  }, [mode]);
};
